import { z } from 'zod';

/**
 * Contrato de entrada da Edge `search` (T2.9, PRD §5.1.1) — fonte única para a function e o app.
 *
 * Só depende de `zod`: a Edge importa este arquivo por caminho relativo no Deno, que não resolve
 * `@powerful/config`. Por isso os tipos de estabelecimento são repetidos aqui, com teste de
 * paridade contra `business.ts`.
 *
 * Os limites espelham os da SQL (`search_businesses`, T2.7), que os revalida — o schema existe
 * para responder 400 cedo e com código i18n, não como única barreira.
 */
export const SEARCH_ESTABLISHMENT_TYPES = ['salon', 'barbershop', 'studio', 'clinic', 'spa'] as const;
export const SEARCH_SORTS = ['relevance', 'distance', 'rating', 'popular', 'recent'] as const;
export const SEARCH_AVAILABILITY = ['today', 'tomorrow', 'week'] as const;
export const SEARCH_CURSOR_MAX = 512;
export const SEARCH_LIMIT_DEFAULT = 20;

const coordinate = { lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) };

export const searchRequestSchema = z
  .object({
    query: z.string().trim().max(60).optional(),
    origin: z.object(coordinate).strict().optional(),
    radiusKm: z.number().min(1).max(100).optional(),
    // [minLng, minLat, maxLng, maxLat]
    bbox: z
      .tuple([
        coordinate.lng,
        coordinate.lat,
        coordinate.lng,
        coordinate.lat,
      ])
      .refine(([minLng, minLat, maxLng, maxLat]) => minLng < maxLng && minLat < maxLat)
      .optional(),
    categoryIds: z.array(z.string().uuid()).max(13).optional(),
    minRating: z.number().min(1).max(5).optional(),
    priceMinCents: z.number().int().min(0).optional(),
    priceMaxCents: z.number().int().min(0).optional(),
    availability: z.enum(SEARCH_AVAILABILITY).optional(),
    languages: z.array(z.string().regex(/^[a-z]{2}$/)).max(12).optional(),
    types: z.array(z.enum(SEARCH_ESTABLISHMENT_TYPES)).max(SEARCH_ESTABLISHMENT_TYPES.length).optional(),
    sort: z.enum(SEARCH_SORTS).default('relevance'),
    limit: z.number().int().min(1).max(50).default(SEARCH_LIMIT_DEFAULT),
    cursor: z.string().min(1).max(SEARCH_CURSOR_MAX).optional(),
  })
  .strict()
  .refine((v) => v.priceMinCents == null || v.priceMaxCents == null || v.priceMinCents <= v.priceMaxCents, {
    path: ['priceMaxCents'],
  })
  .refine((v) => v.radiusKm == null || v.origin != null, { path: ['radiusKm'] });

export type SearchRequest = z.output<typeof searchRequestSchema>;
export type SearchRequestInput = z.input<typeof searchRequestSchema>;
export type SearchSort = (typeof SEARCH_SORTS)[number];
export type SearchAvailability = (typeof SEARCH_AVAILABILITY)[number];
